import { useQuery } from '@tanstack/react-query'
import { Plus, UsersRound } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CLASS_CYCLES } from '@/features/school/vocabulary'
import { tAny } from '@/i18n'
import { useGuardians } from '@/features/auth/guardians'
import { Link, useNavigate } from 'react-router'
import { EmptyState } from '@/components/feedback/States'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { FilterBar, SearchInput } from '@/components/tables/FilterBar'
import { Pagination } from '@/components/tables/Pagination'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { classesQuery } from '@/features/academic/api'
import { Can } from '@/features/auth/guards'
import { studentsQuery } from '@/features/students/api'
import { studentStatusTone } from '@/features/students/status'
import { STUDENT_STATUSES, type StudentSummary } from '@/features/students/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useListParams } from '@/hooks/useListParams'
import { formatDate, humanize } from '@/utils/format'
import { sectionLabel } from '@/features/academic/sectionLabel'

const FILTERS = ['classId', 'sectionId', 'cycle', 'status', 'gender'] as const

export function StudentsListPage() {
  const { t } = useTranslation('students')
  const withGuardians = useGuardians()
  useDocumentTitle('Students')
  const navigate = useNavigate()
  const { state, query, update, toggleSort, setPage } = useListParams(FILTERS, { sort: 'name' })
  const list = useQuery(studentsQuery(query))
  const classes = useQuery(classesQuery())

  const sections = useMemo(
    () => classes.data?.find((c) => String(c.id) === state.filters.classId)?.sections ?? [],
    [classes.data, state.filters.classId],
  )

  const allColumns: Column<StudentSummary>[] = [
    {
      key: 'name',
      header: t('Student'),
      sortKey: 'name',
      cell: (s) => (
        <div className="flex items-center gap-3">
          <Avatar name={s.fullName} src={s.photoUrl} size="sm" />
          <div className="min-w-0">
            <Link
              to={`/admin/students/${s.id}`}
              onClick={(e) => e.stopPropagation()}
              className="block truncate font-medium text-ink hover:text-brand-700"
            >
              {s.fullName}
            </Link>
            <span className="block text-xs text-muted">{s.studentCode}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'admission',
      header: t('Admission no.'),
      sortKey: 'admissionNumber',
      hideBelow: 'lg',
      cell: (s) => <span className="font-mono text-xs">{s.admissionNumber}</span>,
    },
    {
      key: 'class',
      header: t('Class'),
      sortKey: 'class',
      cell: (s) =>
        s.currentEnrollment ? (
          <span>
            {s.currentEnrollment.className}
            {s.currentEnrollment.sectionName ? (
              <span className="text-muted"> · {s.currentEnrollment.sectionName}</span>
            ) : null}
            {s.currentEnrollment.rollNumber ? (
              <span className="ml-1.5 text-xs text-subtle">#{s.currentEnrollment.rollNumber}</span>
            ) : null}
          </span>
        ) : (
          <span className="text-subtle">{t('Not enrolled')}</span>
        ),
    },
    { key: 'gender', header: t('Gender'), hideBelow: 'xl', cell: (s) => humanize(s.gender) },
    {
      key: 'guardian',
      header: t('Primary guardian'),
      hideBelow: 'lg',
      cell: (s) =>
        s.primaryGuardian ? (
          <div className="min-w-0">
            <span className="block truncate">{s.primaryGuardian.name}</span>
            <span className="block text-xs text-muted">
              {s.primaryGuardian.phone ?? humanize(s.primaryGuardian.relation)}
            </span>
          </div>
        ) : (
          '—'
        ),
    },
    {
      key: 'admitted',
      header: t('Admitted'),
      sortKey: 'admissionDate',
      hideBelow: 'xl',
      cell: (s) => formatDate(s.admissionDate),
    },
    {
      key: 'status',
      header: t('Status'),
      sortKey: 'status',
      cell: (s) => (
        <Badge tone={studentStatusTone[s.status]} dot size="sm">
          {humanize(s.status)}
        </Badge>
      ),
    },
  ]
  const columns = withGuardians ? allColumns : allColumns.filter((c) => c.key !== 'guardian')

  const hasFilters = Boolean(state.search || Object.keys(state.filters).length)

  return (
    <>
      <PageHeader
        title={t('Students')}
        description={
          withGuardians
            ? t('Everyone on the register, with their current class and guardian.')
            : t('Everyone on the register, with their current class.')
        }
        breadcrumbs={[{ label: t('People') }, { label: t('Students') }]}
        homeHref="/admin"
        actions={
          <Can permission="students.manage">
            <Button asChild leftIcon={<Plus />}>
              <Link to="/admin/students/new">{t('Admit student')}</Link>
            </Button>
          </Can>
        }
      />
      <Card>
        <FilterBar
          actions={
            hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  update({
                    search: undefined,
                    classId: undefined,
                    sectionId: undefined,
                    status: undefined,
                    gender: undefined,
                  })
                }
              >
                {t('Clear filters')}
              </Button>
            ) : null
          }
        >
          <SearchInput
            value={state.search}
            onChange={(v) => update({ search: v })}
            placeholder={t('Name, code, admission no. or phone')}
            label={t('Search students')}
          />
          <Select
            aria-label={t('Filter by class')}
            className="sm:w-auto sm:min-w-40"
            placeholder={t('All classes')}
            value={state.filters.classId ?? ''}
            onChange={(e) => update({ classId: e.target.value, sectionId: undefined })}
            options={(classes.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
          />
          <Select
            aria-label={t('Filter by section')}
            className="sm:w-auto sm:min-w-36"
            placeholder={t('All sections')}
            disabled={!state.filters.classId}
            value={state.filters.sectionId ?? ''}
            onChange={(e) => update({ sectionId: e.target.value })}
            options={sections.map((s) => ({
              value: s.id,
              label: s.name ? t('Section {{name}}', { name: s.name }) : t('Whole class'),
            }))}
          />
          {(classes.data ?? []).some((c) => c.cycle) ? (
            <Select
              aria-label={t('Filter by cycle')}
              className="sm:w-auto sm:min-w-36"
              placeholder={t('All cycles')}
              value={state.filters.cycle ?? ''}
              onChange={(e) => update({ cycle: e.target.value })}
              options={CLASS_CYCLES.filter((c) => (classes.data ?? []).some((k) => k.cycle === c.value)).map(
                (c) => ({
                  value: c.value,
                  label: tAny(c.label),
                }),
              )}
            />
          ) : null}
          <Select
            aria-label={t('Filter by status')}
            className="sm:w-auto sm:min-w-36"
            placeholder={t('Any status')}
            value={state.filters.status ?? ''}
            onChange={(e) => update({ status: e.target.value })}
            options={STUDENT_STATUSES.map((s) => ({ value: s, label: humanize(s) }))}
          />
          <Select
            aria-label={t('Filter by gender')}
            className="sm:w-auto sm:min-w-32"
            placeholder={t('Any gender')}
            value={state.filters.gender ?? ''}
            onChange={(e) => update({ gender: e.target.value })}
            options={['female', 'male', 'other'].map((g) => ({ value: g, label: humanize(g) }))}
          />
        </FilterBar>
        <DataTable
          caption={t('Students')}
          columns={columns}
          rows={list.data?.data}
          getRowId={(s) => s.id}
          loading={list.isPending}
          error={list.error}
          onRetry={() => void list.refetch()}
          sort={{ key: state.sort, direction: state.direction }}
          onSortChange={toggleSort}
          onRowClick={(s) => void navigate(`/admin/students/${s.id}`)}
          mobileCard={(s) => (
            <div className="flex items-center gap-3">
              <Avatar name={s.fullName} src={s.photoUrl} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">{s.fullName}</p>
                <p className="truncate text-xs text-muted">
                  {s.currentEnrollment
                    ? sectionLabel(s.currentEnrollment.className, s.currentEnrollment.sectionName, ' · ')
                    : t('Not enrolled')}{' '}
                  · {s.studentCode}
                </p>
              </div>
              <Badge tone={studentStatusTone[s.status]} size="sm">
                {humanize(s.status)}
              </Badge>
            </div>
          )}
          empty={
            <EmptyState
              icon={<UsersRound />}
              title={hasFilters ? t('No students match these filters') : t('No students yet')}
              description={
                hasFilters
                  ? t('Try a different search or clear the filters.')
                  : t('Admit your first student to get started.')
              }
              compact
            />
          }
        />
        <Pagination
          meta={list.data?.meta}
          onPageChange={setPage}
          onPerPageChange={(n) => update({ perPage: n })}
        />
      </Card>
    </>
  )
}
